<template>
  <section class="step">
    <div class="inner">
      <back-button v-if="!banner" class="back-button" />
      <header
        class="header"
        :class="[banner ? 'header--has-banner' : '']"
        v-bind:style="{ backgroundColor: color }"
      >
        <step-index class="step-index" />
        <div v-if="banner">
          <smart-image :src="banner" :alt="title" />
        </div>
        <div v-else ref="header" class="inner-header">
          <div ref="headerIcon" class="step-icon">
            <smart-image :src="displayedImage" :alt="title" />
          </div>
          <h2
            ref="headerTitle"
            class="step-title"
            :class="[displayedTitle.length > 75 ? 'step-title--small' : '']"
            v-html="displayedTitle"
          ></h2>
        </div>
      </header>
      <div
        class="content"
        v-bind:style="{ borderColor: hexToRgba(color, 0.5) }"
      >
        <div v-if="banner">
          <back-button class="back-button" />
          <h2
            ref="headerTitle"
            class="step-title step-title--inside"
            :class="[displayedTitle.length > 75 ? 'step-title--small' : '']"
            v-html="displayedTitle"
          ></h2>
        </div>
        <component
          :is="Input"
          :name="title"
          :options="options"
          :params="typeParameters"
          :multi="type === 'multi'"
          @change="onOptionChange"
          :key="title"
          :selected-option="selectedOption"
          :gtm="gtm"
        />
        <div
          class="vue-calc-btn-wrapper"
          v-if="showSubmit && !typeParameters.buttonInside"
        >
          <next-step-button
            :disabled="!ready"
            :selected-option="selectedOption"
            :gtm="gtm"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { TweenMax, Power4 } from "gsap/TweenMax";
import hexToRgba from "@/mixins/hexToRgba";
import Choice from "./fields/Choice.vue";
import Slider from "./fields/Slider.vue";
import Switch from "./fields/Switch.vue";
// import MultiChoice from "./fields/MultiChoice.vue";
import BackButton from "./ui/BackButton.vue";
import StepIndex from "./ui/StepIndex.vue";
import StepProgressbar from "./ui/StepProgressbar.vue";
import SmartImage from "@/components/ui/SmartImage";
import NextStepButton from "@/components/ui/NextStepButton";
import isAbsoluteUrl from "@/utils/isAbsoluteUrl";

export default {
  mixins: [hexToRgba],
  components: {
    BackButton,
    StepIndex,
    StepProgressbar,
    SmartImage,
    NextStepButton
  },
  props: {
    title: {
      type: String,
      required: true
    },
    image: {
      type: String,
      required: false
    },
    color: {
      type: String,
      required: false,
      default: "#fff"
    },
    type: {
      type: String,
      required: true
    },
    options: {
      type: [Array, Object],
      required: true
    },
    result: {
      type: Object,
      required: false
    },
    typeParameters: {
      type: Object,
      required: false,
      default: () => new Object()
    },
    name: {
      type: String,
      required: false
    }
  },
  data() {
    return {
      ready: false,
      selectedOption: null,
      displayedImage: this.image,
      displayedTitle: this.title,
      publicPath: process.env.VUE_APP_BASE_URL
        ? process.env.VUE_APP_BASE_URL
        : process.env.BASE_URL
    };
  },
  computed: {
    Input() {
      switch (this.type) {
        case "choice":
          return Choice;
        case "slider":
          return Slider;
        case "switch":
          return Switch;
        case "multi":
          return Choice;
      }
      return null;
    },

    showSubmit() {
      return this.type != "choice";
    },
    currentStepIndex() {
      return this.$store.getters.currentStepIndex;
    },
    nextStepLabel() {
      return this.$store.getters.nextStepLabel;
    },
    banner() {
      return this.$store.getters.banner;
    },
    totalSteps() {
      return this.$store.getters.totalSteps;
    },
    currentBrancheId() {
      let currentBrancheId = this.$store.getters.deepestAttributeValueInResults(
        "brancheId"
      );
      return currentBrancheId ? currentBrancheId : "";
    },
    gtm() {
      if (this.selectedOption) {
        let label = this.selectedOption.gtmLabel || this.selectedOption.title;

        if (Array.isArray(this.selectedOption)) {
          label = this.selectedOption
            .map(option => option.gtmLabel || option.title)
            .join(",");
        }

        return {
          action:
            this.currentBrancheId +
            "-" +
            this.currentStepIndex +
            "/" +
            this.totalSteps +
            " - " +
            this.title,
          label
        };
      } else {
        return {};
      }
    }
  },
  mounted() {
    this.temporaryDisable();
  },
  methods: {
    temporaryDisable() {
      this.ready = false;
      setTimeout(() => {
        this.ready = true;
      }, 500);
    },
    onOptionChange(option) {
      this.selectedOption = option;

      if (!this.showSubmit) {
        this.submitOption();
      }
    },
    submitOption() {
      this.$pushGTM(this.gtm);

      // Submit option
      this.$store.dispatch("submitOption", this.selectedOption);
      this.temporaryDisable();
    }
  },
  watch: {
    title() {
      if (this.$refs.header || this.banner) {
        this.displayedTitle = this.title;
        return;
      }

      let prevHeight = this.$refs.header.offsetHeight;
      TweenMax.set(this.$refs.header, {
        height: prevHeight
      });
      TweenMax.staggerTo(
        [this.$refs.headerIcon, this.$refs.headerTitle],
        0.2,
        {
          x: -100,
          opacity: 0
        },
        0.12,
        () => {
          let newImg = new Image();
          newImg.onload = () => {
            this.displayedTitle = this.title;

            TweenMax.set(this.$refs.header, {
              height: "auto"
            });

            TweenMax.from(this.$refs.header, 0.6, {
              height: prevHeight,
              ease: Power4.easeInOut,
              delay: 0.15
            });

            TweenMax.staggerFromTo(
              [this.$refs.headerIcon, this.$refs.headerTitle],
              0.3,
              {
                delay: 0.05,
                x: 100,
                opacity: 0
              },
              {
                x: 0,
                opacity: 1
              },
              0.15
            );
          };
          newImg.src = isAbsoluteUrl(this.image)
            ? this.image
            : this.publicPath + this.image;
          this.displayedImage = this.image;
        }
      );
    },
    name() {
      this.selectedOption = null;
    }
  }
};
</script>

<style lang="scss" scoped>
.step {
  background: $c-background;
  display: flex;
  flex-direction: column;
  align-items: stretch;

  @include media(">=tablet") {
    border-radius: $s-border-radius;
    padding: 12px;
    box-shadow: $s-box-shadow-big;
  }
}

.inner {
  position: relative;
}

.content {
  overflow: hidden !important;
  all: inherit;
  position: relative;
  border: 2px solid $c-gray-light;
  border-top: none;
  padding: 20px;

  @include media(">=tablet") {
    border-radius: 0 0 $s-border-radius $s-border-radius;
  }

  @include media(">=desktop") {
    padding: 25px 50px;
    min-height: 274px;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .eficiensCalculatorApp--media & {
    min-height: 518px;
  }
}

.header {
  position: relative;
  align-self: stretch;
  padding: 60px 20px 20px;
  transition: background-color 1s $s-transition-easing-bounce;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 274px;

  @include media(">=tablet") {
    border-radius: $s-border-radius $s-border-radius 0 0;
  }

  @include media(">=desktop") {
    min-height: 216px;
    padding: 50px 30px 10px;
  }

  &--has-banner {
    padding: 0;
    min-height: 0;
    overflow: hidden;

    img {
      width: 100%;
      height: auto;
      display: block;
    }
  }
}

.inner-header {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  @include media(">=desktop") {
    flex-direction: row;
  }
}

.step-icon {
  display: block;
  position: relative;
  margin-right: 25px;
  text-align: center;

  img {
    height: auto;
    width: auto;
    max-height: 100px !important;
    margin: 0 auto;

    @include media(">=tablet") {
      max-width: 100% !important;
      max-height: 130px;
    }
  }
}

.step-title {
  max-width: 510px;
  font-size: 23px;
  font-weight: bold;
  white-space: pre-wrap;
  text-align: center;
  margin-bottom: 0;
  color: $c-text;
  font-family: $f-title;
  text-transform: none;

  &--small {
    font-size: 18px;
  }

  &--inside {
    text-align: center;
    align-self: stretch;
    padding-bottom: 20px;
    max-width: none;
  }

  @include media(">=tablet") {
    font-size: 35px;

    &--small {
      font-size: 25px;
    }

    &--inside {
      padding: 15px 180px 25px;
      font-size: 32px;
    }
  }

  @include media(">=desktop") {
    font-size: 40px;
    text-align: left;
    margin-top: 0;

    &--small {
      font-size: 30px;
    }

    &--inside {
      text-align: center;
      font-size: 32px;
    }
  }
}

.step-progressbar {
  margin-bottom: 30px;
}

.back-button {
  position: relative;
  z-index: 1;

  @include media(">=tablet") {
    position: absolute;
    top: 0;
    left: 0;
  }
}

.step-index {
  position: absolute;
  top: -53px;
  left: 50%;
  transform: translateX(-50%);

  .header--has-banner & {
    top: auto;
    bottom: 0;
    right: 0;
    left: auto;
    transform: none;
    background: $c-primary-contrast;
    color: $c-primary;
    border-radius: 20px 0 0 0;
    padding: 5px 10px;
    height: auto;
    font-size: 30px;

    @include media("<tablet") {
      position: static;
      border-radius: 0;
      font-size: 23px;
    }
  }
}

.vue-calc-btn-wrapper {
  margin-top: 50px;
  text-align: center;

  .eficiensCalculatorApp--media & {
    margin-top: 30px;
  }
}
</style>
